import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  Button,
  Navbar as BootstrapNavbar,
  Nav,
  Container,
  NavDropdown,
  Dropdown,
} from "react-bootstrap";
import NotificationsDropdown from "./components/NotificationsDropdown";
import AnnouncementsCounter from "./components/AnnouncementsCounter";
import { API_ENDPOINTS } from './config/api';
import { Sun, Moon } from 'lucide-react';

const Navbar = ({ pageTitle }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const isLoggedIn = !!localStorage.getItem('token');
  const role = localStorage.getItem('role');

  const getDashboardPath = () => {
    if (!isLoggedIn) return '/';
    switch (role) {
      case 'resident':
        return '/resident/dashboard';
      case 'attending':
        return '/attending/dashboard';
      case 'director':
        return '/dashboard';
      case 'admin':
        return '/admin/dashboard';
      default:
        return '/';
    }
  };
  const [profileDropdownOpen, setProfileDropdownOpen] = useState(false);
  const [userData, setUserData] = useState({
    firstName: localStorage.getItem("firstName"),
    lastName: localStorage.getItem("lastName"),
    profilePic: localStorage.getItem("profilePic"),
    role: localStorage.getItem("role"),
    email: localStorage.getItem("email"),
  });
  const [profilePicDataUrl, setProfilePicDataUrl] = useState(null);
  const [darkMode, setDarkMode] = useState(() => {
    const stored = localStorage.getItem("theme");
    if (stored) return stored === "dark";
    return true; // default to dark
  });

  // Function to load profile picture with authentication
  const loadProfilePicture = async (profilePicPath) => {
    if (!profilePicPath) return null;
    
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(`${API_ENDPOINTS.PROFILE_PICS}${profilePicPath}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      
      if (!response.ok) {
        console.warn('Failed to load profile picture:', response.status);
        return null;
      }
      
      const blob = await response.blob();
      return URL.createObjectURL(blob);
    } catch (error) {
      console.error('Error loading profile picture:', error);
      return null;
    }
  };

  // Function to generate fallback avatar
  const generateFallbackAvatar = (firstName, lastName) => {
    const firstInitial = firstName?.charAt(0) || 'U';
    const lastInitial = lastName?.charAt(0) || 'S';
    return `data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='24' height='24' viewBox='0 0 100 100'%3E%3Crect width='100' height='100' fill='%23007bff'/%3E%3Ctext x='50' y='50' text-anchor='middle' dy='.3em' font-size='40' fill='white'%3E${firstInitial}${lastInitial}%3C/text%3E%3C/svg%3E`;
  };

  useEffect(() => {
    const fetchUserData = async () => {
      try {
        const token = localStorage.getItem("token");
        if (!token) return;

        const response = await fetch(`${API_ENDPOINTS.AUTH}/me`, {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        });

        if (response.ok) {
          const data = await response.json();
          setUserData({
            firstName: data.firstName,
            lastName: data.lastName,
            profilePic: data.profilePic,
            role: data.role,
            email: data.email,
          });
          
          // Load profile picture if it exists
          if (data.profilePic) {
            const picUrl = await loadProfilePicture(data.profilePic);
            setProfilePicDataUrl(picUrl);
          }
          
          // Update localStorage
          localStorage.setItem("firstName", data.firstName);
          localStorage.setItem("lastName", data.lastName);
          localStorage.setItem("profilePic", data.profilePic || "");
          localStorage.setItem("role", data.role);
          if (data.email) {
            localStorage.setItem("email", data.email);
          }
        }
      } catch (error) {
        console.error("Error fetching user data:", error);
      }
    };

    fetchUserData();
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      const profileDropdown = document.getElementById("profile-dropdown");
      const profileButton = document.getElementById("profile-button");

      if (
        profileDropdownOpen &&
        profileDropdown &&
        !profileDropdown.contains(event.target) &&
        !profileButton.contains(event.target)
      ) {
        setProfileDropdownOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [profileDropdownOpen]);

  useEffect(() => {
    // Pages where dark mode should not be applied
    const excludedPages = ['/', '/privacy', '/contact', '/terms', '/about'];
    const currentPath = location.pathname;
    const shouldExcludeDarkMode = excludedPages.includes(currentPath);
    
    if (shouldExcludeDarkMode) {
      document.body.setAttribute("data-bs-theme", "light");
    } else {
      document.body.setAttribute("data-bs-theme", darkMode ? "dark" : "light");
    }
    
    localStorage.setItem("theme", darkMode ? "dark" : "light");
  }, [darkMode, location.pathname]);

  // Add CSS to remove dropdown arrow and style active nav links
  useEffect(() => {
    const style = document.createElement('style');
    style.textContent = `
      .no-arrow::after {
        display: none !important;
      }
      .navbar-nav .nav-link.active {
        color: var(--blue) !important;
      }
      .navbar-nav .nav-link.active:hover {
        color: var(--blue) !important;
      }
      .navbar-nav .nav-dropdown.active > .nav-link {
        color: var(--blue) !important;
      }
      .navbar-nav .dropdown-item.active {
        color: var(--blue) !important;
        background-color: transparent !important;
      }
    `;
    document.head.appendChild(style);
    return () => document.head.removeChild(style);
  }, []);

  const handleLogout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("firstName");
    localStorage.removeItem("lastName");
    localStorage.removeItem("profilePic");
    localStorage.removeItem("role");
    localStorage.removeItem("documentRequirementsCompleted");
    localStorage.removeItem("notifications");
    localStorage.removeItem("lastNotificationTime");
    window.dispatchEvent(new CustomEvent('app-logout'));
    navigate("/");
  };

  const displayName =
    userData.firstName && userData.lastName
      ? `${userData.firstName} ${userData.lastName}`
      : userData.role === "resident"
      ? "Resident"
      : "Director";

  // Function to get initials
  const getInitials = (firstName, lastName) => {
    if (!firstName || !lastName)
      return userData.role === "resident" ? "R" : "D";
    return `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase();
  };

  // Function to check if a nav link is active
  const isActive = (path) => {
    const currentPath = location.pathname;
    // Handle exact matches and path starts with
    if (path === '/dashboard' || path === '/resident/dashboard' || path === '/attending/dashboard') {
      return currentPath === path;
    }
    // For other paths, check if current path starts with the nav path
    return currentPath.startsWith(path);
  };

  // Extract "tab" from hash fragments like "#tab=vacation"
  const currentHashTab = (() => {
    const rawHash = location.hash ? location.hash.replace(/^#/, "") : "";
    if (!rawHash) return null;
    const params = new URLSearchParams(rawHash);
    return params.get("tab");
  })();

  return (
    <>
      <BootstrapNavbar bg="dark" variant="dark" expand="lg" className="mb-0 nav-border-btm">
        <Container>
          <BootstrapNavbar.Brand
            //onClick={() => navigate(getDashboardPath())} 
            className="fw-bold"
            //style={{ cursor: 'pointer' }}
          >
            <img src="/images/asset4.svg" alt="brand logo" style={{ height: "14px", marginTop: "-8px"}}/>
            {/*<img src="/images/logo4.svg" alt="brand logo" style={{ width: "32px", height: "32px", marginTop: "-4px"}}/>
            <span className="ms-2">medhive</span>*/}
          </BootstrapNavbar.Brand>
          
          {/* Mobile Controls - Notification and Hamburger */}
          <div className="d-lg-none d-flex align-items-center">
            <div className="d-flex align-items-center gap-2 me-2">
              <Button
                variant="light-lite"
                size="sm"
                data-theme-toggle="false"
                className=" text-dark d-flex align-items-center"
                onClick={() => setDarkMode((prev) => !prev)}
                title={darkMode ? "Switch to Light Mode" : "Switch to Dark Mode"}
              >
                {darkMode ? (
                  <Sun/>
                ) : (
                  <Moon/>
                )}
                {darkMode ? "" : ""}
              </Button>
               <NotificationsDropdown />
              <Dropdown  align="end">
                <Dropdown.Toggle variant="light-lite" className="no-arrow p-0" size="sm">
                  <div className=" d-flex justify-content-center flex-row  align-items-center gap-2">
                    {profilePicDataUrl ? (
                      <img
                        src={profilePicDataUrl}
                        alt="Profile"
                        className="profile-pic"
                      />
                    ) : (
                      <div
                      className="profile-pic"
                      >
                        {getInitials(userData.firstName, userData.lastName)}
                      </div>
                    )}
                  </div>
                </Dropdown.Toggle>
                <Dropdown.Menu align="end">
                  <Dropdown.Header>
                   <div className=" d-flex justify-content-start flex-row  align-items-center gap-2">
                    {profilePicDataUrl ? (
                      <img
                        src={profilePicDataUrl}
                        alt="Profile"
                        className="profile-pic"
                      />
                    ) : (
                      <div
                      className="profile-pic"
                      >
                        {getInitials(userData.firstName, userData.lastName)}
                      </div>
                    )}
                     <div className="d-flex flex-column lh-sm">
                       <span className="fw-semibold">{displayName.charAt(0).toUpperCase() + displayName.slice(1)}</span>
                       <span>{userData.email}</span>
                     </div>
                                      
                    </div>
                  </Dropdown.Header>
                  <Dropdown.Divider />
                  <Dropdown.Item href="/profile?tab=profile">Profile</Dropdown.Item>
                  <Dropdown.Item href="/profile?tab=documents">Documents</Dropdown.Item>
                  <Dropdown.Item href="/profile?tab=settings">Settings</Dropdown.Item>
                  <Dropdown.Divider />
                  <Dropdown.Item onClick={handleLogout}>
                    Logout
                  </Dropdown.Item>
                </Dropdown.Menu>
              </Dropdown>
            </div>
            <BootstrapNavbar.Toggle aria-controls="basic-navbar-nav" className="py-2">
              <i className="bi bi-list"></i>
            </BootstrapNavbar.Toggle>
          </div>
          
          <BootstrapNavbar.Collapse id="basic-navbar-nav" className="dtn">
            <Nav className="me-auto">
              {userData.role === "director" && (
                <>
                  <Nav.Link href="/dashboard" active={isActive("/dashboard")}>Dashboard</Nav.Link>
                  <Nav.Link href="/users" active={isActive("/users")}>Users</Nav.Link>
                  <Nav.Link href="/evaluations" active={isActive("/evaluations")}>Evaluations</Nav.Link>
                  <Nav.Link href="/calendar" active={isActive("/calendar")}>Calendar</Nav.Link>
                  <Nav.Link href="/onboarding" active={isActive("/onboarding")}>Onboarding</Nav.Link>
                  <Nav.Link href="/rotation-scheduler" active={isActive("/rotation-scheduler")}>
                    Schedule
                  </Nav.Link>
                  <Nav.Link href="/conference-scheduler" active={isActive("/conference-scheduler")}>
                    Conferences
                  </Nav.Link>
                  <Nav.Link href="/resources" active={isActive("/resources")}>
                    Resources
                  </Nav.Link>
                  <Nav.Link href="/discussions" active={isActive("/discussions")}>
                    Discussions
                  </Nav.Link>
                  <Nav.Link href="/guides" active={isActive("/guides")}>
                    Guides
                  </Nav.Link>
                  <NavDropdown title="Logs" active={isActive("/director/work-logs") || isActive("/director/case-summary")}>
                    <NavDropdown.Item
                      href="/director/work-logs"
                      active={location.pathname === "/director/work-logs" && currentHashTab === null}
                    >
                      Work Hour Logs
                    </NavDropdown.Item>
                    <NavDropdown.Item
                      href="/director/work-logs#tab=vacation"
                      active={location.pathname === "/director/work-logs" && currentHashTab === "vacation"}
                    >
                      Leave requests
                    </NavDropdown.Item>
                    <NavDropdown.Item
                      href="/director/work-logs#tab=leave"
                      active={location.pathname === "/director/work-logs" && currentHashTab === "leave"}
                    >
                      Leave tracking
                    </NavDropdown.Item>
                    <NavDropdown.Item href="/director/case-summary" active={isActive("/director/case-summary")}>
                      Case Logs
                    </NavDropdown.Item>
                  </NavDropdown>
                </>
              )}
              {userData.role === "resident" && (
                <>
                  <Nav.Link href="/resident/dashboard" active={isActive("/resident/dashboard")}>Home</Nav.Link>
                  <Nav.Link href="/resident/evaluation" active={isActive("/resident/evaluation")}>
                    Evaluations
                  </Nav.Link>
                  <Nav.Link href="/calendar" active={isActive("/calendar")}>Calendar</Nav.Link>
                  <Nav.Link href="/onboarding" active={isActive("/onboarding")}>Onboarding</Nav.Link>
                  <Nav.Link href="/resident-rotation-scheduler" active={isActive("/resident-rotation-scheduler")}>
                    Schedule
                  </Nav.Link>
                  <Nav.Link href="/conference-scheduler" active={isActive("/conference-scheduler")}>
                    Conferences
                  </Nav.Link>
                  <Nav.Link href="/resources" active={isActive("/resources")}>
                    Resources
                  </Nav.Link>
                  <Nav.Link href="/discussions" active={isActive("/discussions")}>
                    Discussions
                  </Nav.Link>
                  <Nav.Link href="/guides" active={isActive("/guides")}>
                    Guides
                  </Nav.Link>
                  <NavDropdown title="Logs" active={isActive("/resident/work-hours") || isActive("/resident/case-log-page")}>
                    <NavDropdown.Item
                      onClick={() => window.location.href = '/resident/work-hours#tab=work&weekOffset=0'}
                      active={location.pathname === "/resident/work-hours" && currentHashTab === null}
                    >
                      Work Logs
                    </NavDropdown.Item>
                    <NavDropdown.Item
                      onClick={() => window.location.href = '/resident/work-hours#tab=vacation'}
                      active={location.pathname === "/resident/work-hours" && currentHashTab === "vacation"}
                    >
                      Leave requests
                    </NavDropdown.Item>
                    <NavDropdown.Item onClick={() => window.location.href = '/resident/case-log-page'} active={isActive("/resident/case-log-page")}>
                      Case Logs
                    </NavDropdown.Item>
                  </NavDropdown>
                </>

              )}
              {userData.role === "attending" && (
                <>
                  <Nav.Link href="/attending/dashboard" active={isActive("/attending/dashboard")}>Home</Nav.Link>
                  <Nav.Link href="/attending/evaluations" active={isActive("/attending/evaluations")}>
                    My Evaluations
                  </Nav.Link>
                  <Nav.Link href="/calendar" active={isActive("/calendar")}>Calendar</Nav.Link>
                  <Nav.Link href="/onboarding" active={isActive("/onboarding")}>Onboarding</Nav.Link>
                  <Nav.Link href="/conference-scheduler" active={isActive("/conference-scheduler")}>
                    Conferences
                  </Nav.Link>
                  <Nav.Link href="/resources" active={isActive("/resources")}>
                    Resources
                  </Nav.Link>
                  <Nav.Link href="/discussions" active={isActive("/discussions")}>
                    Discussions
                  </Nav.Link>
                  <Nav.Link href="/guides" active={isActive("/guides")}>
                    Guides
                  </Nav.Link>
                </>
              )}
            </Nav>

            <Nav className="align-items-center gap-2">
              {/* Announcements button + counter (desktop) */}
              <div className="d-none d-md-block">
                <AnnouncementsCounter />
              </div>

              {/* Desktop Dark Mode Toggle */}
              <Button
                variant="light-lite"
                size="sm"
                data-theme-toggle="false"
                className=" text-dark d-flex align-items-center d-none d-md-block"
                onClick={() => setDarkMode((prev) => !prev)}
                title={darkMode ? "Switch to Light Mode" : "Switch to Dark Mode"}
              >
                {darkMode ? (
                   <Sun fill="var(--white)"/>
                ) : (
                  <Moon fill="var(--white)"/>
                )}
              </Button>       

              {/* Desktop Notification Dropdown */}
              <div className="d-none d-lg-block">
                <NotificationsDropdown />
              </div>

              <Dropdown  align="end" className="d-none d-md-block">
                <Dropdown.Toggle variant="light-lite" className="no-arrow p-0 rounded-pill" size="sm">
                  <div className=" d-flex justify-content-center flex-row  align-items-center gap-2">
                    {profilePicDataUrl ? (
                      <img
                        src={profilePicDataUrl}
                        alt="Profile"
                        className="profile-pic"
                      />
                    ) : (
                      <div
                      className="profile-pic"
                      >
                        {getInitials(userData.firstName, userData.lastName)}
                      </div>
                    )}
                  </div>
                </Dropdown.Toggle>
                <Dropdown.Menu align="end">
                  <Dropdown.Header >
                   <div className=" d-flex justify-content-start flex-row  align-items-center gap-2">
                    {profilePicDataUrl ? (
                      <img
                        src={profilePicDataUrl}
                        alt="Profile"
                        className="profile-pic"
                      />
                    ) : (
                      <div
                      className="profile-pic"
                      >
                        {getInitials(userData.firstName, userData.lastName)}
                      </div>
                    )}
                     <div className="d-flex flex-column lh-sm">
                       <span className="fw-semibold" style={{ color: 'var(--gray-800)' }}>{displayName.charAt(0).toUpperCase() + displayName.slice(1)}</span>
                       <span>{userData.email}</span>
                     </div>
                                      
                    </div>
                  </Dropdown.Header>
                  <Dropdown.Divider />
                  <Dropdown.Item href="/profile?tab=profile">Profile</Dropdown.Item>
                  <Dropdown.Item href="/profile?tab=documents">Documents</Dropdown.Item>
                  <Dropdown.Item href="/profile?tab=settings">Settings</Dropdown.Item>
                  <Dropdown.Divider />
                  <Dropdown.Item onClick={handleLogout}>
                    Logout
                  </Dropdown.Item>
                </Dropdown.Menu>
              </Dropdown>
            </Nav>
          </BootstrapNavbar.Collapse>
        </Container>
      </BootstrapNavbar>

      {/* Secondary Page Title Bar */}
      {!location.pathname.includes("/evaluation") &&
        !location.pathname.includes("/survey-results") &&
        !location.pathname.includes("/performance-history") &&
        !location.pathname.includes("/users") &&
        !location.pathname.includes("/director/work-logs") &&
        !location.pathname.includes("/director/work-log") &&
        !location.pathname.includes("/resident/work-hours") &&
        !location.pathname.includes("/resident/case-log-page") &&
        !location.pathname.includes("/calendar") &&
        !location.pathname.includes("/conference-scheduler") &&
        !location.pathname.includes("/resident/conferences") &&
        !location.pathname.includes("/attending/conferences") &&
        !location.pathname.includes("/onboarding") &&
        !location.pathname.includes("/rotation-scheduler") &&
        !location.pathname.includes("/completed-evaluation") &&
        !location.pathname.includes("/resident-rotation-scheduler") &&
        !location.pathname.includes("/discussions") &&
        !location.pathname.includes("/resident-profile") &&
        !location.pathname.includes("/resources") &&
        !location.pathname.includes("/guides") && (
          <div className="bg-white shadow-sm custom-sticky-top">
            <Container>
              <div className="d-flex justify-content-between align-items-center">
                {(() => {
                  const dashboardPaths = [
                    { role: "director", path: "/dashboard" },
                    { role: "attending", path: "/attending/dashboard" },
                    { role: "resident", path: "/resident/dashboard" },
                    { role: "administrative assistant", path: "/admin/dashboard" },
                    { role: "medical student", path: "/medical-student/dashboard" }
                  ];
                  const isDashboard = dashboardPaths.some(
                    ({ role, path }) => userData.role === role && location.pathname.includes(path)
                  );
                  return isDashboard ? (
                    <h6 className="py-3 fw-semibold mb-0">
                      <i className="bi bi-stars text-warning"></i> Welcome  
                      
                        <span className="ms-1 d-none d-md-inline">
                          {userData.firstName?.charAt(0).toUpperCase() + userData.firstName?.slice(1)}{" "}
                          {userData.lastName?.charAt(0).toUpperCase() + userData.lastName?.slice(1)}
                        </span>
                        <span className="ms-1 d-inline d-md-none">
                          {userData.firstName?.charAt(0).toUpperCase() + userData.firstName?.slice(1)}
                        </span>
                      
                    </h6>
                  ) : (
                    <h6 className="py-3 mb-0 fw-semibold">{pageTitle}</h6>
                  );
                })()}
                
                {(userData.role === "director" && location.pathname.includes("/dashboard")) ||
                (userData.role === "attending" && location.pathname.includes("/attending/dashboard")) ||
                (userData.role === "resident" && location.pathname.includes("/resident/dashboard")) ||
                (userData.role === "administrative assistant" && location.pathname.includes("/admin/dashboard")) ||
                (userData.role === "medical student" && location.pathname.includes("/medical-student/dashboard")) ?
                <span className="d-flex align-items-center justify-content-end">
                  <span className="d-none d-md-inline small fw-semibold text-muted">
                    {new Date().toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
                  </span>
                  <span className="d-inline d-md-none small fw-semibold text-muted">
                    {new Date().toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                  </span>
                  <span className="badge bg-primary rounded-3 fw-semibold ms-2"> <i className="bi bi-calendar-event"></i></span>

                </span> : ''}
              </div>
            </Container>
          </div>
        )}
    </>
  );
};

export default Navbar;
